import axios from 'axios';

// Auto logout after 30 minutes of inactivity
let inactivityTimer = null;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

const resetInactivityTimer = () => {
  // Clear existing timer
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  // Only set timer if user is logged in (has token or adminToken)
  if (localStorage.getItem('token') || localStorage.getItem('adminToken')) {
    inactivityTimer = setTimeout(() => {
      // Clear authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      // Redirect to login
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }
};

// Track user activity events
const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

// Initialize activity tracking
const initActivityTracking = () => {
  // Reset timer on any user activity
  activityEvents.forEach(event => {
    document.addEventListener(event, resetInactivityTimer, true);
  });

  // Start initial timer
  resetInactivityTimer();
};

// Clean up activity tracking
const cleanupActivityTracking = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }

  activityEvents.forEach(event => {
    document.removeEventListener(event, resetInactivityTimer, true);
  });
};

// Initialize activity tracking when module loads
if (typeof window !== 'undefined') {
  initActivityTracking();
}

// Dynamically determine backend URL based on current host
const getBackendURL = () => {
  const currentHost = window.location.hostname;

  // If accessing from localhost, use localhost for backend
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return 'http://localhost:8080/api';
  }
  
  // If accessing from custom domain (www.collaborb.com)
  if (currentHost === 'www.collaborb.com' || currentHost === 'collaborb.com') {
    return 'https://www.collaborb.com/api';
  }
  
  // If accessing from ngrok URL (contains .ngrok-free.app, .ngrok.io, or .ngrok.app)
  if (currentHost.includes('.ngrok.app')) {
    return 'https://collaborb-frontend.ngrok.app/api';
  }

  if(currentHost.includes('collaborb-frontend.s3-website.ap-south-1.amazonaws.com')){
    return 'http://3.108.190.110:8080/api';
  }
  
  // If accessing from any other IP (like phone on same network), use that IP for backend
  return `http://${currentHost}:8000/api`;
};

const api = axios.create({
  baseURL: getBackendURL(),
  timeout: 30000, // 30 seconds timeout for NgRok
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth
api.interceptors.request.use((config) => {
  // Check for both regular user token and admin token
  const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Reset inactivity timer on API calls (user activity)
    resetInactivityTimer();
  }
  return config;
});

// Add response interceptor for better error handling and retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle auth errors first
    if (error.response?.status === 401) {
      // Token expired or invalid, clear localStorage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('userId');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      // Clean up activity tracking since user is being logged out
      cleanupActivityTracking();

      // Redirect to appropriate login page
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      } else {
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    // Retry logic for network errors (common with NgRok)
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest.timeout = 45000; // Increase timeout for retry
        return api(originalRequest);
      }
    }
    
    // Handle NgRok tunnel errors
    if (error.response?.status === 503 || error.response?.status === 502) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        return api(originalRequest);
      }
    }
    
    return Promise.reject(error);
  }
);

// Sponsorship API
export const sponsorshipAPI = {
  createSponsorship: async (data) => {
    const response = await api.post('/sponsorships', data);
    return response.data;
  },

  updateSponsorship: async (id, data) => {
    const response = await api.put(`/sponsorships/${id}`, data);
    return response.data;
  },

  publishSponsorship: async (id) => {
    const response = await api.post(`/sponsorships/${id}/publish`);
    return response.data;
  },

  getAllSponsorships: async (status = null, userId = null) => {
    const params = {};
    if (status) params.status = status;
    if (userId) params.userId = userId;
    const response = await api.get('/sponsorships', { params });
    return response.data;
  },

  getSponsorshipById: async (id) => {
    const response = await api.get(`/sponsorships/${id}`);
    return response.data;
  },

  createInvestment: async (sponsorshipId, data) => {
    const response = await api.post(`/sponsorships/${sponsorshipId}/investments`, data);
    return response.data;
  },

  getInvestments: async (sponsorshipId) => {
    const response = await api.get(`/sponsorships/${sponsorshipId}/investments`);
    return response.data;
  }
};

export const collaboratorClubAPI = {
  getEligibleProjects: async (userId) => {
    const response = await api.get('/collaborator-club/projects/eligible', { params: { userId } });
    return response.data;
  },

  createPost: async (userId, payload) => {
    const response = await api.post('/collaborator-club/posts', payload, { params: { userId } });
    return response.data;
  },

  getVisiblePosts: async (userId) => {
    const response = await api.get('/collaborator-club/posts', { params: { userId } });
    return response.data;
  },

  getMyPosts: async (userId) => {
    const response = await api.get('/collaborator-club/posts/mine', { params: { userId } });
    return response.data;
  },

  getPostDetails: async (postId, userId) => {
    const response = await api.get(`/collaborator-club/posts/${postId}`, { params: { userId } });
    return response.data;
  },

  requestToJoin: async (postId, userId, message) => {
    const response = await api.post(`/collaborator-club/posts/${postId}/requests`, { message }, { params: { userId } });
    return response.data;
  },

  getJoinRequests: async (postId, userId) => {
    const response = await api.get(`/collaborator-club/posts/${postId}/requests`, { params: { userId } });
    return response.data;
  },

  approveRequest: async (requestId, userId) => {
    const response = await api.post(`/collaborator-club/requests/${requestId}/approve`, {}, { params: { userId } });
    return response.data;
  },

  rejectRequest: async (requestId, userId) => {
    const response = await api.post(`/collaborator-club/requests/${requestId}/reject`, {}, { params: { userId } });
    return response.data;
  },

  getTasks: async (postId, userId) => {
    const response = await api.get(`/collaborator-club/posts/${postId}/tasks`, { params: { userId } });
    return response.data;
  },

  createTask: async (postId, userId, payload) => {
    const response = await api.post(`/collaborator-club/posts/${postId}/tasks`, payload, { params: { userId } });
    return response.data;
  },

  updateTaskStatus: async (taskId, userId, status) => {
    const response = await api.put(`/collaborator-club/tasks/${taskId}/status`, { status }, { params: { userId } });
    return response.data;
  },

  createSubtask: async (taskId, userId, payload) => {
    const response = await api.post(`/collaborator-club/tasks/${taskId}/subtasks`, payload, { params: { userId } });
    return response.data;
  },

  updateSubtaskStatus: async (subtaskId, userId, status) => {
    const response = await api.put(`/collaborator-club/subtasks/${subtaskId}/status`, { status }, { params: { userId } });
    return response.data;
  }
};

// Export functions for manual control if needed
export { resetInactivityTimer, cleanupActivityTracking, initActivityTracking };
export default api;